import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import { useTheme } from "@mui/material/styles";
import { getAPI } from "../api/callAPI";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { isArray } from "lodash";

const ViewMenu = () => {
  const { storeSlug, storeID } = useParams();
  const navigate = useNavigate();
  const { endpoint } = useSelector((state) => state.auth);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfURL, setPdfURL] = useState("");
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [storeData, setStoreData] = useState(null);
  // const [actualStoreID, setActualStoreID] = useState(null);

  // Fetch store data if storeSlug is provided
  const fetchStoreData = useCallback(async () => {
    if (!storeSlug) return null;
    try {
      const resp = await getAPI(endpoint, `stores/${storeSlug}`);
      const store = resp?.data?.["0"];
      if (store?.id) {
        setStoreData(store);
        // setActualStoreID(store.id);
        return store.id;
      }
      return null;
    } catch (e) {
      console.error("Error fetching store data:", e);
      return null;
    }
  }, [endpoint, storeSlug]);

  // Fetch menus
  const fetchMenus = useCallback(async (storeId) => {
    if (!storeId) {
      setError("Invalid store id");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const resp = await getAPI(endpoint, `store/menu/${storeId}`);
      
      // Handle both single menu and array of menus
      let menuData = resp?.data;
      let menuList = [];
      
      if (isArray(menuData) && menuData.length > 0) {
        menuList = menuData;
      } else if (menuData) {
        menuList = [menuData];
      }
      
      if (menuList.length === 0) {
        throw new Error("Menu not available");
      }
      
      setMenus(menuList);
      
      // If single menu, display it directly
      if (menuList.length === 1) {
        const menu = menuList[0];
        const url = menu?.uploaded_menu || menu?.pdf_url || "";
        if (!url) throw new Error("Menu PDF not available");
        setPdfURL(url);
        setSelectedMenu(menu);
      }
      // If multiple menus, don't set PDF URL yet - wait for user selection
    } catch (e) {
      setError(e?.message || "Unable to load menu");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  // Handle menu selection
  const handleMenuSelect = (menu) => {
    const url = menu?.uploaded_menu || menu?.pdf_url || "";
    if (url) {
      setPdfURL(url);
      setSelectedMenu(menu);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      let idToUse = storeID;
      
      // If storeSlug is provided, fetch store data first
      if (storeSlug && !storeID) {
        const fetchedId = await fetchStoreData();
        if (cancelled) return;
        if (!fetchedId) {
          setError("Store not found");
          setLoading(false);
          return;
        }
        idToUse = fetchedId;
      }

      // Fetch menus
      if (idToUse) {
        await fetchMenus(idToUse);
      } else {
        setError("Invalid store identifier");
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [storeSlug, storeID, fetchStoreData, fetchMenus]);

  const shareURL = useMemo(() => {
    if (storeSlug) {
      return window?.location?.origin + "/web/" + String(storeSlug || "") + "/menu";
    }
    return window?.location?.origin + "/menu/" + String(storeID || "");
  }, [storeSlug, storeID]);

  // Set dynamic page title
  useDocumentTitle(storeData?.name ? `${storeData.name} - Menu` : "View Menu");

  const handleGoBack = () => {
    if (storeSlug) {
      navigate(`/web/${storeSlug}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={handleGoBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            {storeData?.name ? `${storeData.name} - Menu` : "View Our Menu"}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading menu...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
            <Button variant="outlined" onClick={handleGoBack}>
              Go Back
            </Button>
          </Box>
        ) : menus.length > 0 ? (
          <>
            {menus.length > 1 && !selectedMenu ? (
              // Show menu list if multiple menus and none selected
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                    p: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.main}05 100%)`,
                    borderRadius: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                    }}
                  >
                    <MenuBookIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Available Menus
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {menus.map((menu, index) => (
                    <Card
                      key={menu.id || index}
                      elevation={0}
                      sx={{
                        borderRadius: "12px",
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 4px 20px ${theme.palette.primary.main}20`,
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleMenuSelect(menu)}
                        sx={{
                          padding: { xs: "16px", sm: "20px" },
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        <CardContent sx={{ padding: "0 !important" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                            }}
                          >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  fontSize: { xs: "1rem", sm: "1.125rem" },
                                  mb: 0.5,
                                  lineHeight: 1.3,
                                }}
                              >
                                {menu.name || menu.title || menu.menu_name || "Menu"}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: "0.75rem",
                                  display: "block",
                                }}
                              >
                                Click to view menu
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: theme.palette.primary.main,
                                transition: "transform 0.2s ease",
                                ".MuiCardActionArea:hover &": {
                                  transform: "translateX(4px)",
                                },
                              }}
                            >
                              <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
                            </Box>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>
              </Box>
            ) : pdfURL ? (
              // Show PDF viewer when a menu is selected
              <Box>
                {menus.length > 1 && selectedMenu && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        setSelectedMenu(null);
                        setPdfURL("");
                      }}
                    >
                      Back to Menu List
                    </Button>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      {selectedMenu?.name || selectedMenu?.title || selectedMenu?.menu_name || "Menu"}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Shareable link: {shareURL}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    href={pdfURL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in New Tab
                  </Button>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    height: "80vh",
                    minHeight: "600px",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src={`${pdfURL}#toolbar=1`}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                    title="Menu PDF"
                  />
                </Box>
              </Box>
            ) : null}
          </>
        ) : null}
      </Paper>
    </Container>
  );
};

export default ViewMenu;


